import { put, take, takeLatest } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { firestoreRef } from '../utils/firebase';
import {
    SinglePageActions,
    fetchSinglePageReturn,
    updatePageSettingsSuccess,
    updatingPageSettings,
    updatePageSettingsFailed,
    clearUpdatePageSettings,
    savePageContentsDone,
    clearSavePageContents
} from '../actions/singlePage.actions';
import { get, set, unset, merge } from 'lodash';
import { pageUtils, authenticationUtils, logUtils } from '../utils/common';

const pagesCollection = firestoreRef
    .collection('content')
    .doc('en')
    .collection('pages_tree');

function* fetchSinglePageSaga(action) {
    const rootPath = action.payload[0];
    let pathChunks = action.payload;
    pathChunks.shift();
    const channel = new eventChannel(emitter => {
        const listener = pagesCollection
            .doc(rootPath)
            .get()
            .then(rootPage => {
                let curPageData = rootPage.data();
                while (pathChunks.length > 0) {
                    curPageData = curPageData.subpages[pathChunks.shift()];
                }

                emitter({ data: curPageData || false });
            });

        return () => {
            listener.off();
        };
    });

    while (true) {
        let { data } = yield take(channel);
        if (!data) {
            yield put(fetchSinglePageReturn({ found: false }));
        } else {
            data.found = true;
            yield put(fetchSinglePageReturn(data));
        }
    }
}

function* savePageContentsSaga(action) {
    const page = action.payload;
    const newPageData = {
        templateConf: page.templateConf,
        statusProps: {
            lastSaved: {
                user: authenticationUtils.getUserEmail(),
                utcTimeStamp: new Date().toUTCString()
            }
        }
    };
    const pageRootPath = pageUtils.getRootFormPath(page.path);
    const pagePathStructure = pageUtils.getObjectStructureFromPath(page.path);

    //Put the page data in the correct page object structure
    let updatedPageObject = {};
    if (pagePathStructure === '') {
        updatedPageObject = newPageData;
    } else {
        set(updatedPageObject, pagePathStructure, newPageData);
    }

    let success = yield pagesCollection
        .doc(pageRootPath)
        .set(updatedPageObject, { merge: true })
        .then(() => true)
        .catch(() => false);

    if (success === true) {
        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.PAGE_CONTENT_UPDATED, { path: page.path });
    }

    yield put(savePageContentsDone(success));
    yield put(clearSavePageContents());
}

function* updatePageSettingsSaga(action) {
    yield put(updatingPageSettings());

    let success = false;
    let { page, newPageData, index } = action.payload;
    const newPagePathStructure = pageUtils.getObjectStructureFromPath(newPageData.path);
    const newPageRootPath = pageUtils.getRootFormPath(newPageData.path);

    const pagePathStructure = pageUtils.getObjectStructureFromPath(page.path);
    const pageRootPath = pageUtils.getRootFormPath(page.path);
    const pageParentPath = pageUtils.getParentFormPath(page.path);

    newPageData.statusProps = {
        lastSaved: {
            user: authenticationUtils.getUserEmail(),
            utcTimeStamp: new Date().toUTCString()
        }
    };

    if(newPageData.parentPath == null){
        newPageData.parentPath = "";
    }

    const pathChanged = newPageData.title !== page.title || newPageData.parentPath !== pageParentPath;

    newPageData.subpages = {};
    if (pathChanged) {
        //Update subpages paths
        for (let subpageKey in page.subpages) {
            newPageData.subpages[subpageKey] = pageUtils.updateParentPath(page.subpages[subpageKey], newPageData.path);
        }
    }

    //Check if new page already exists
    // -1 : mising root page.
    //  0 : page is new, doesn't already exists.
    //  1 : page with the same path already exists.
    let pageExists = pathChanged
        ? yield pagesCollection
              .doc(newPageRootPath)
              .get()
              .then(rootPage => {
                  if (!rootPage.exists) {
                      return -1;
                  } else {
                      return undefined === get(rootPage.data(), newPagePathStructure) ? 0 : 1;
                  }
              })
              .catch(err => {
                  return true;
              })
        : 1;

    success =
        (yield firestoreRef
            .runTransaction(transaction => {
                return transaction.get(pagesCollection.doc(pageRootPath)).then(rootPage => {
                    let rootData = rootPage.data();
                    let curPageData = pagePathStructure === '' ? rootData : get(rootPage.data(), pagePathStructure);
                    let updatedPageObject = {};

                    //Copy the missing page properties from the current page object.
                    let updatedPageData = merge(curPageData, newPageData);

                    //Delete a util-property
                    delete updatedPageData.parentPath;

                    //Put the page data in the correct page object structure
                    if (pageExists === -1 || pageUtils.isRootPagePath(updatedPageData.path)) {
                        updatedPageObject = updatedPageData;
                    } else {
                        set(updatedPageObject, newPagePathStructure, updatedPageData);
                    }

                    if (pathChanged) {
                        //Remove the old page data
                        if (pagePathStructure === '') {
                            transaction.delete(pagesCollection.doc(pageRootPath));
                        } else {
                            unset(rootData, pagePathStructure);
                            transaction.set(pagesCollection.doc(pageRootPath), rootData);
                        }
                    }

                    //Do the update
                    transaction.set(pagesCollection.doc(newPageRootPath), updatedPageObject, { merge: true });
                });
            })
            .then(() => true)
            .catch(err => false));

    if (success) {
        if (page.path === index) {
            const indexResult = yield updateIndex(newPageData.path);
            if (!indexResult) {
                console.error('Could not update index');
            } else {
                logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.INDEX_UPDATED, { index: newPageData.path });
            }
        }

        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.UPDATE_PAGE_SETTINGS, { old: page, new: { title: newPageData.title, path: newPageData.path, description: newPageData.description } });
        yield put(updatePageSettingsSuccess(newPageData.path));
        console.log('update success');
    } else {
        yield put(updatePageSettingsFailed(page.path));
        console.log('update failed');
    }

    yield put(clearUpdatePageSettings());
}

function* updateIndex(newIndex) {
    const result = yield firestoreRef
        .collection('content')
        .doc('en')
        .update({ index: newIndex })
        .then(doc => {
            return true;
        })
        .catch(err => {
            return false;
        });

    return result;
}

export function* singlePageSaga() {
    yield takeLatest(SinglePageActions.FETCH_SINGLE_PAGE, fetchSinglePageSaga);
    yield takeLatest(SinglePageActions.SAVE_PAGE_CONTENTS, savePageContentsSaga);
    yield takeLatest(SinglePageActions.UPDATE_PAGE_SETTINGS, updatePageSettingsSaga);
}
